/**
 * Ayuda — Ventas → Presupuestos
 */

/** @type {import('../help-index.js').HelpSidebarItem} */
export const ADM_SAL_PRESUPUESTOS_ITEM = {
  id: 'presupuestos',
  screenId: 'adm-sal-presupuestos',
  label: 'Presupuestos',
  description: 'Creá, buscá y consultá presupuestos para tus clientes.',
  detail: {
    sections: [
      {
        title: 'Para qué sirve',
        intro:
          'Un presupuesto es una propuesta de venta que le hacés a un cliente: qué productos, a qué precio, por cuánto tiempo es válida y en qué condiciones. Podés guardarlo, consultarlo, modificarlo, imprimirlo o eliminarlo.',
      },
      {
        title: 'Paso 1 — Buscar presupuestos',
        intro: 'Arriba de la lista hay filtros para encontrar presupuestos ya cargados:',
        fields: [
          {
            label: 'Fecha Desde / Fecha Hasta',
            description: 'Limitá la búsqueda a un rango de fechas. Podés usar uno solo o los dos.',
          },
          {
            label: 'Cliente',
            description: 'Escribí el nombre del cliente y elegilo de la lista que aparece. Así ves solo sus presupuestos.',
          },
          {
            label: 'Número',
            description: 'Si conocés el número del presupuesto, escribilo acá para ir directo a ese comprobante.',
          },
          {
            label: 'Botón Buscar',
            description: 'Tocá «Buscar» para actualizar la lista con los filtros que hayas puesto.',
          },
        ],
      },
      {
        title: 'Paso 2 — Leer la lista',
        intro: 'Después de buscar (o al entrar por primera vez si ya hay datos), la lista muestra:',
        bullets: [
          'Fecha — cuándo se emitió el presupuesto.',
          'Validez — hasta qué fecha ofrece esos precios o condiciones.',
          'Cliente — a quién va dirigido.',
          'Número — identificador del presupuesto.',
          'Total — importe total.',
          'Moneda — pesos, dólares u otra moneda usada.',
          'Ícono de lápiz — modificar el presupuesto.',
          'Ícono de cruz (×) — eliminar el presupuesto.',
        ],
        paragraphs: [
          'Tocá una fila (en cualquier parte que no sea el lápiz ni la cruz) para ver el detalle completo.',
          'Si hay muchos resultados, podés cambiar de página abajo de la lista o descargar la información.',
        ],
      },
      {
        title: 'Paso 3 — Cargar un presupuesto nuevo',
        intro:
          'El ícono verde + abre la ventana «Nuevo Presupuesto». Completá los datos y, al final, tocá «Guardar presupuesto».',
      },
      {
        title: 'Datos generales del presupuesto',
        fields: [
          {
            label: 'Fecha',
            description: 'Fecha de emisión del presupuesto.',
          },
          {
            label: 'Validez',
            description: 'Hasta qué fecha el cliente puede aceptar esa oferta. Después conviene emitir uno nuevo.',
          },
          {
            label: 'Condición de pago',
            description: 'Cómo se paga si el cliente acepta (por ejemplo «30 días», «contado», «50 % seña y saldo contra entrega»).',
          },
        ],
      },
      {
        title: 'Cliente',
        intro:
          'Buscá y elegí el cliente de la lista. Si no está cargado, podés ingresar los datos en el mismo formulario (nombre, documento, condición de IVA, etc.), igual que al dar de alta un cliente nuevo. Al elegir un cliente, el sistema puede traer su lista de precios y calcular impuestos que correspondan.',
      },
      {
        title: 'Condiciones comerciales',
        fields: [
          {
            label: 'Lista de precios',
            description:
              'Qué lista de precios usar para los productos. Si el cliente tiene una asignada, puede seleccionarse sola. Al cambiar la lista, los precios de los artículos ya cargados se actualizan.',
          },
          {
            label: 'Moneda',
            description:
              'En qué moneda está expresado el presupuesto. Si elegís una moneda distinta de pesos, el sistema trae la cotización del día.',
          },
          {
            label: 'Discriminar IVA en precios',
            description:
              'Solo aparece si tu empresa es Responsable Inscripto. Marcada, los precios muestran neto e IVA por separado en cada línea y en el resumen.',
          },
        ],
      },
      {
        title: 'Artículos (productos)',
        intro: 'Acá cargás qué le ofrecés al cliente. Cada fila es un producto:',
        fields: [
          {
            label: 'Producto',
            description: 'Buscá el artículo por nombre o código. Al elegirlo, se completan precio y datos impositivos según la lista de precios.',
          },
          {
            label: 'Cant.',
            description: 'Cuántas unidades.',
          },
          {
            label: 'P. unit.',
            description: 'Precio por unidad. Puede venir de la lista de precios; si tenés permiso, podés cambiarlo a mano.',
          },
          {
            label: 'Neto / Subtotal',
            description: 'El importe de esa línea, calculado automáticamente según cantidad y precio.',
          },
          {
            label: 'IVA (por línea)',
            description: 'Si discriminás IVA, podés elegir la alícuota (21 %, 10,5 %, etc.) en productos gravados.',
          },
          {
            label: 'Botón + (comentario)',
            description: 'Agrega un texto extra para esa línea (por ejemplo «Incluye instalación»).',
          },
          {
            label: 'Botón ×',
            description: 'Quita esa línea del presupuesto (si hay más de una).',
          },
          {
            label: '+ Agregar línea',
            description: 'Sumá otro producto al presupuesto.',
          },
        ],
        paragraphs: [
          'Si hay promociones activas, pueden aparecer descuentos automáticos o productos de regalo. También podés elegir promociones disponibles en cada línea o sobre el total.',
          'Los productos de regalo se muestran aparte, con subtotal cero.',
        ],
      },
      {
        title: 'Percepciones impositivas',
        intro:
          'Si el cliente está sujeto a percepciones (por ejemplo IIBB), el sistema las calcula al elegirlo. Podés revisar el porcentaje y el importe antes de guardar.',
      },
      {
        title: 'Observaciones y resumen',
        fields: [
          {
            label: 'Observaciones',
            description: 'Notas generales que quieras que figuren en el presupuesto (texto libre, opcional).',
          },
          {
            label: 'Resumen (a la derecha)',
            description:
              'Muestra netos, IVA, percepciones si las hay y el total final. Revisalo antes de guardar.',
          },
        ],
        paragraphs: [
          'Tocá «Guardar presupuesto» para crear uno nuevo, o «Guardar cambios» si estás editando. El sistema avisa si falta el cliente, algún producto o la moneda.',
        ],
      },
      {
        title: 'Ver el detalle de un presupuesto',
        intro: 'Al tocar una fila de la lista se abre «Detalle de Presupuesto». Ahí ves:',
        bullets: [
          'Número, fecha, validez y condición de pago.',
          'Datos del cliente y moneda.',
          'Lista de artículos con cantidades, precios y subtotales.',
          'Observaciones y totales (neto, IVA, percepciones, total).',
          'Quién lo creó y, si hubo cambios, quién lo modificó.',
        ],
        paragraphs: [
          'El botón «Imprimir» genera un PDF del presupuesto para entregar al cliente o archivar.',
        ],
      },
      {
        title: 'Modificar o eliminar',
        bullets: [
          'Lápiz en la lista — abre el mismo formulario con los datos cargados para corregir o actualizar.',
          'Cruz (×) en la lista — pide confirmación («¿Está seguro…?»). Si tocás «SI», el presupuesto se elimina; con «NO» se cancela.',
        ],
      },
    ],
  },
}
